import moment, { Moment, unitOfTime } from "moment";
import Papa from "papaparse";
import customToast, { ToastOptions } from "../components/shared/services/toaster-service";
import useConfigs from "../hooks/use-config";
import { getLanguage } from "./language-helper";
import { findErrorMessage } from "./metadata";
import { ColSizeType } from "./common-types";
import { getLabel } from "../hooks/use-labels";
import { ListFiltersType } from "./filter-types";
import { defaultPagination, FILTERS_KEY_NAME, FILTERS_PARAM_NAME } from "./filter-utils";

const PNF = require("google-libphonenumber").PhoneNumberFormat;

type FileData = {
  [key: string]: any;
};

export const getFileExtension: any = {
  "image/png": "png",
  "image/jpeg": "jpeg",
  "image/jpg": "jpg",
  "application/pdf": "pdf",
};
export const phoneInstance = () => {
  return require("google-libphonenumber").PhoneNumberUtil.getInstance();
};

export const formatPhoneNumber = (phoneNumber: string, countryDialingCode: string) => {
  const phoneUtil = phoneInstance();
  const number = phoneUtil.parseAndKeepRawInput(phoneNumber, "US");

  return `${countryDialingCode} ${phoneUtil.format(number, PNF.NATIONAL)}`;
};

export const getLocalPhone = (phoneNumber: string, countryCode: string) => {
  const phoneUtil = phoneInstance();
  const number = phoneUtil.parseAndKeepRawInput(phoneNumber, "US");

  return number.getNationalNumber().toString();
};

export const timeDiff = (from: any, to: any, unit: unitOfTime.Diff = "seconds"): number => {
  return moment(from).diff(to, unit);
};

export const timeDiffFromNow = (time: Moment, unit = "seconds"): number => {
  return timeDiff(time, moment(), unit as unitOfTime.Diff);
};

export const isJSON = (val: string): boolean => {
  if (typeof val === "string" && val.length > 1) {
    const startChar = val[0];
    const endChar = val[val.length - 1];
    return ["{", "["].includes(startChar) && ["]", "}"].includes(endChar);
  }

  return false;
};

export const previewMaskSsn = (ssnNumber: string) => {
  const lastFour = ssnNumber?.slice(ssnNumber.length - 4);
  return `*****${lastFour ?? ""}`;
};

export const previewLastFour = (str: any) => {
  const lastFour = str?.slice(str.length - 4);
  return `.....${lastFour}`;
};

export const getApiErrorMessage = (error: any): string => {
  if (typeof error === "string") {
    return error;
  }

  const responseData = error?.response?.data;
  if (responseData) {
    if (responseData.errors?.message) {
      return responseData.errors.message;
    }
    if (typeof responseData.message === "string" && responseData.message) {
      return responseData.message;
    }
    if (responseData.errors?.title) {
      return responseData.errors.title;
    }
  }

  return error?.message ?? "";
};

export const showErrorNotification = (error: any, options?: ToastOptions) => {
  if (typeof error === "string") {
    customToast.error(error, options);
    return;
  }

  let message = "";

  if (error?.error_code) {
    message = findErrorMessage(error.error_code, getLanguage());
    if (!message) {
      console.error(`Could not find any message against [${error.error_code}]`);
    }
  }

  if (!message) {
    message = getApiErrorMessage(error);
  }

  customToast.error(message);
};

export const showSuccessNotification = (success: any) => {
  if (typeof success === "string") {
    customToast.success(success);
  } else {
    let message = "";
    if (success.error_code) {
      message = findErrorMessage(success.error_code, getLanguage());
      if (!message) {
        console.error(`Could not find any message against [${success.error_code}]`);
      }
    }

    if (!message) {
      message = success.message;
    }

    customToast.success(message);
  }
};

export const addSpaces = (text: string) => {
  return text.split(/(\w{4})/).join(" ");
};

export const isValidCurrencyCode = (currency: string): boolean => {
  if (!currency) return false;
  try {
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    });
    return true;
  } catch (error) {
    console.error("Currency Formatting Error", error);
    return false;
  }
};

export const formatCurrency = (amount: number, currency: string) => {
  if (!currency || currency === "null") {
    return amount || "-";
  }
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  });

  //return amount < 0 ? `${formatter.format(amount * -1)}` : formatter.format(amount)
  return formatter.format(amount);
};

export const formatAmountWithoutCurrency = (amount: number): string => {
  return amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const isVisibleMenu = (key: string, role?: string, allowedRole?: string) => {
  return key && key.toLowerCase() === "y" && role === allowedRole;
};

export const parseCSV = (file: any, onSuccess: any) => {
  Papa.parse(file, {
    header: true,
    skipEmptyLines: true,
    encoding: "ISO-8859-1",
    complete: (results) => {
      onSuccess({ ...results });
    },
  });
};

export const formatExcelDataAsObject = (data: FileData[]): FileData[] => {
  return data.map((row) => {
    const updatedRow: FileData = {};

    Object.keys(row).forEach((key) => {
      const value = row[key];

      if (value instanceof Date) {
        updatedRow[key] = formatExcelDate(value);
      } else if (typeof value === "number") {
        updatedRow[key] = value.toString();
      } else {
        updatedRow[key] = value;
      }
    });

    return updatedRow;
  });
};

const formatExcelDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getPartnerNames = (partners: any, partnerId: string) => {
  const partner = partners.find((item: any) => item.id === partnerId);
  if (partner) {
    const { legalName, dbaName } = partner;
    return dbaName || legalName;
  }

  return "-";
};

export const getAffiliateName = (affiliates: any, affiliateId: string) => {
  const affiliate = affiliates.find((item: any) => item.id === affiliateId);

  if (affiliate) {
    const { businessProfile } = affiliate;
    return businessProfile ? businessProfile.dbaName || businessProfile.legalName : "-";
  }

  return "-";
};

export const capitalize = (word: string) => {
  return word ? word[0].toUpperCase() + word.slice(1).toLowerCase() : "";
};

export const StateName = (stateCode: string) => {
  const { states } = useConfigs(["states"]);
  const state = states.find(({ code }: any) => code === stateCode);
  return state?.name || stateCode;
};

export const CountryName = (countryCode: string) => {
  const { countries } = useConfigs(["countries"]);
  const country = countries.find(({ code }: any) => code === countryCode);
  return country?.name || countryCode;
};

export const secondsToHms = (d: any) => {
  d = Number(d);
  const hours = Math.floor(d / 3600);
  const minutes = Math.floor((d % 3600) / 60);
  const seconds = Math.floor((d % 3600) % 60);

  const hDisplay = hours > 0 ? formatTime(hours) + ":" : "";
  const mDisplay = minutes > 0 ? formatTime(minutes) + ":" : "00:";
  const sDisplay = seconds > 0 ? formatTime(seconds) : "00";
  return hDisplay + mDisplay + sDisplay;
};

export const formatTime = (time: number) => {
  if (time < 10) {
    return `0${time}`;
  }
  return time;
};

export const getCard = (cardId: string, cards: any) => {
  return cards.find((item: any) => item.id === cardId);
};

export const titleCase = (str: string) => {
  const splitStr = str.toLowerCase().split(" ");
  for (let i = 0; i < splitStr.length; i++) {
    splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
  }
  return splitStr.join(" ");
};

export const getStorageItem = (key: string): any => {
  const item = localStorage.getItem(key);

  if (item) return JSON.parse(item);

  return null;
};

const MULTI_AFFILIATE_ALLOWED_ROLES = ["PP_PAYROLL_MANAGER", "PP_PAYROLL_MANAGER_ADMIN"];

const normalizeRoleCode = (role: string): string => {
  const normalized = role.trim().toUpperCase().replace(/\s+/g, "_");
  return normalized.startsWith("PP_") ? normalized : `PP_${normalized}`;
};

export const isMultiAffiliateEligibleRole = (role?: string | null): boolean => {
  if (!role) return false;
  return MULTI_AFFILIATE_ALLOWED_ROLES.includes(normalizeRoleCode(role));
};

/**
 * Parses the raw `selectedAffiliateId` value that was stored via
 * `JSON.stringify`.  Returns the string id when present and non-empty,
 * otherwise `null`.  No role check is performed — this is intentionally a
 * thin storage helper so it can be reused by the Redux reducer (which runs
 * before role checks) without circular imports.
 */
export const readSelectedAffiliateIdFromStorage = (): string | null => {
  const raw = localStorage.getItem("selectedAffiliateId");
  if (!raw) return null;
  try {
    const v = JSON.parse(raw);
    return typeof v === "string" && v.length > 0 ? v : null;
  } catch {
    return null;
  }
};

/** Active employer/affiliate from the top-bar switcher (multi-affiliate users). */
export const getSelectedAffiliateId = (): string | null => {
  const currentUserRole = getStorageItem("userRole");
  if (!isMultiAffiliateEligibleRole(currentUserRole)) {
    return null;
  }
  return readSelectedAffiliateIdFromStorage();
};

export const getBrandIdName = (): string => {
  const brandProps = getStorageItem("init");
  if (!isEmpty(brandProps)) {
    const { auth_app_id } = brandProps;
    return auth_app_id.toLowerCase();
  }

  return "consumer";
};

export const setClassName = (containerClass: string, customClass: string) => {
  return customClass ? containerClass + " " + customClass : containerClass;
};

export const colSizes = (
  xl: ColSizeType,
  lg: ColSizeType,
  md: ColSizeType,
  sm: ColSizeType,
  xs: ColSizeType,
) => {
  return {
    xl,
    lg,
    md,
    sm,
    xs,
  };
};

export const checkImageSrc = (key: string): string => {
  const assetPath = "/assets/images";
  let imgSrc: string;

  if (getLabel(key) && getLabel(key).includes("http")) {
    imgSrc = getLabel(key);
  } else if (getLabel(key)) {
    imgSrc = `${assetPath}/${getLabel(key)}`;
  } else {
    imgSrc = isImage(key) ? `${assetPath}/${key}` : "";
  }
  return imgSrc;
};

const isImage = (url: string) => {
  return /\.(jpg|jpeg|png|webp|avif|gif|svg|ico)$/.test(url);
};

export const getPlanePhoneNumber = (countryDialingCode: string, phoneNumber: string) => {
  return `+${countryDialingCode}${phoneNumber}`;
};

export const validDateWithinRange = (
  date: string,
  format: string,
  minRange: number,
  maxRange: number,
) => {
  const formattedDate = moment(date, format, true).startOf("day");
  let error = "";
  let valid = true;
  if (!formattedDate.isValid()) {
    error = getLabel("INVALID_DATE_FORMAT");
    valid = false;
    return { error, valid };
  }

  const diff = moment().startOf("day").diff(formattedDate, "year", true);
  if (diff > maxRange || minRange > diff) {
    error = getLabel("INVALID_AGE_RANGE")?.replace("{MIN}", minRange)?.replace("{MAX}", maxRange);
    valid = false;
  }
  return { error, valid };
};

export const getFileExt: { [key: string]: string } = {
  PNG: "image/png",
  png: "image/png",
  JPEG: "image/jpeg",
  jpeg: "image/png",
  JPG: "image/jpg",
  jpg: "image/jpg",
  PDF: "application/pdf",
  pdf: "application/pdf",
};

export const getContentType = (key: string) => {
  return getFileExt[key.toUpperCase()];
};

export const setClasses = (mainClass: string, customClass?: string) => {
  return customClass ? mainClass + " " + customClass : mainClass;
};

export const sizeInMB = (sizeInBytes: number) => Number((sizeInBytes * (1024 * 1024)).toFixed(2));

export const getFormattedFileName = (data: any, downloadFileDataType?: number): string => {
  const currentDate = new Date();
  const formattedDate = currentDate.toISOString().replace(/[-T:]/g, "").slice(0, 14);
  let fileNamePrefix = "consumers-";
  if (downloadFileDataType === 2) {
    fileNamePrefix = "load-funds-";
  } else if (downloadFileDataType === 3) {
    fileNamePrefix = "transactions-";
  } else if (downloadFileDataType === 4) {
    if (data.fileName) {
      return (data?.fileName).replace(/\.[^/.]+$/, "");
    }
    fileNamePrefix = `${getLabel("employersFileNamePrefix")}-`;
  }
  let filename = "";
  if (data?.enrollmentBatchId) {
    filename = `${fileNamePrefix}${data.enrollmentBatchId}-${formattedDate}`;
  } else {
    filename = `${fileNamePrefix}${formattedDate}`;
  }
  return filename;
};

export const setListingFilters = (filters: ListFiltersType) => {
  localStorage.setItem(FILTERS_KEY_NAME, JSON.stringify(filters));
};

export const getFiltersAndPageData = (
  defaultFilter: ListFiltersType["filters"],
  params: URLSearchParams,
) => {
  const filterItem = localStorage.getItem(FILTERS_KEY_NAME);
  if (filterItem) {
    let filterValue: ListFiltersType = JSON.parse(filterItem);
    if (filterValue.filterName === params.get(FILTERS_PARAM_NAME)) {
      return filterValue;
    } else {
      return { filters: defaultFilter, pagination: defaultPagination };
    }
  }
  return { filters: defaultFilter, pagination: defaultPagination };
};

export const checkWebsiteUrl = (urlString: string) => {
  if (!urlString.startsWith("http://") && !urlString.startsWith("https://")) {
    return "https://" + urlString;
  }
  return urlString;
};

export const parseNumericString = (numberString: string | number, defaultValue: number): number => {
  let parsedNumber = defaultValue;
  if (typeof numberString === "string") {
    try {
      parsedNumber = parseInt(numberString);
    } catch (e) {
      console.error(e);
    }
  } else {
    parsedNumber = numberString;
  }
  return parsedNumber;
};

export const truncateFileName = (
  fileName: string,
  maxLength: number,
  extension: string,
): string => {
  if (fileName.length > maxLength) {
    const sliceLength = maxLength - (extension.length + 3);

    const truncatedName = fileName.slice(0, sliceLength);
    return `${truncatedName}...${extension}`;
  }

  return fileName;
};

export const validateDateFields = (
  errors: any,
  dateType: "created" | "approved" | "",
  startDate?: Moment,
  endDate?: Moment,
) => {
  if (startDate && endDate && isValidDateRange(startDate, endDate)) {
    dateType ? delete errors[`${dateType}OnStartDate`] : delete errors["fromDate"];
  }
};

export const isValidDateRange = (startDate: Moment, endDate: Moment) => {
  return startDate.isSameOrBefore(endDate, "day");
};

export const checkDateRangeForErrors = (
  fromDate: string,
  toDate: string,
  maxRange: number,
  format: string = "YYYY-MM-DD",
) => {
  if (!fromDate && !toDate) {
    return { error: "", fromDate, toDate };
  }

  const fromDateMoment = moment(fromDate).startOf("day");
  const toDateMoment = moment(toDate).startOf("day");

  let error = "";

  if (!isValidDateRange(fromDateMoment, toDateMoment)) {
    error = getLabel("fromDateBeforeToDateError");
  } else if (toDateMoment.diff(fromDateMoment, "days", true) > maxRange) {
    error = getLabel("maxDurationError")?.replace("{{days}}", maxRange.toString());
  }
  return { error, fromDate: fromDateMoment.format(format), toDate: toDateMoment.format(format) };
};

type OrderDirection = "asc" | "desc";

type OrderByIteratee<T> = keyof T | ((item: T) => unknown);

const toArray = <T>(value: T | T[]): T[] => (Array.isArray(value) ? value : [value]);

const getOrderByValue = <T>(item: T, iteratee: OrderByIteratee<T>): unknown => {
  if (typeof iteratee === "function") {
    return iteratee(item);
  }
  return (item as Record<string, unknown>)[iteratee as string];
};

const compareOrderByValues = (a: unknown, b: unknown): number => {
  if (a === b) return 0;
  if (a == null) return -1;
  if (b == null) return 1;

  if (typeof a === "string" && typeof b === "string") {
    return a.localeCompare(b);
  }

  if (typeof a === "number" && typeof b === "number") {
    return a - b;
  }

  if (a instanceof Date && b instanceof Date) {
    return a.getTime() - b.getTime();
  }

  return String(a).localeCompare(String(b));
};

export const orderBy = <T>(
  collection: T[] | null | undefined,
  iteratees: OrderByIteratee<T> | OrderByIteratee<T>[] = [],
  orders: OrderDirection | OrderDirection[] = "asc",
): T[] => {
  if (!collection?.length) {
    return collection ? [...collection] : [];
  }

  const iterateeList = toArray(iteratees);
  const orderList = toArray(orders);

  return [...collection].sort((a, b) => {
    for (let i = 0; i < iterateeList.length; i++) {
      const comparison = compareOrderByValues(
        getOrderByValue(a, iterateeList[i]),
        getOrderByValue(b, iterateeList[i]),
      );

      if (comparison !== 0) {
        const order = orderList[i] ?? orderList[orderList.length - 1] ?? "asc";
        return order === "desc" ? -comparison : comparison;
      }
    }
    return 0;
  });
};

export function isEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === "string" || Array.isArray(value)) return value.length === 0;
  if (value instanceof Map || value instanceof Set) {
    return value.size === 0;
  }
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
}

export const isNotEmpty = <T>(value: T | null | undefined): value is NonNullable<T> =>
  !isEmpty(value);

export const isEqual = (value: unknown, other: unknown): boolean => {
  if (value === other) return true;

  if (value == null || other == null || typeof value !== "object" || typeof other !== "object") {
    return false;
  }

  if (value.constructor !== other.constructor) return false;

  if (Array.isArray(value)) {
    if (value.length !== (other as unknown[]).length) {
      return false;
    }
    return value.every((item, index) => isEqual(item, (other as unknown[])[index]));
  }

  if (value instanceof Date) {
    return value.getTime() === (other as Date).getTime();
  }

  if (value instanceof RegExp) {
    return value.toString() === (other as RegExp).toString();
  }

  const keys = Object.keys(value);
  if (keys.length !== Object.keys(other).length) {
    return false;
  }

  return keys.every(
    (key) =>
      Object.prototype.hasOwnProperty.call(other, key) &&
      isEqual((value as Record<string, unknown>)[key], (other as Record<string, unknown>)[key]),
  );
};
